import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, ArrowLeft, GitMerge, Search, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';

const EstadoTag = ({ estado }) => (
  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">{estado}</span>
);

function DuplicateGroup({ group, onMerged, checked, onToggleGroup }) {
  const [open, setOpen] = useState(true);
  const [principalId, setPrincipalId] = useState(group.sugerido_principal_id);
  const [casos, setCasos] = useState(group.casos);
  const [selected, setSelected] = useState(
    () => new Set(group.casos.filter(c => c.id !== group.sugerido_principal_id).map(c => c.id))
  );
  const [merging, setMerging] = useState(false);
  const [searchingCuit, setSearchingCuit] = useState(false);

  const toggleSel = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const setPrincipal = (id) => {
    setPrincipalId(id);
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const cuitDelGrupo = casos.find(c => c.cuit)?.cuit;

  const buscarPorCuit = async () => {
    if (!cuitDelGrupo) { toast.error('Este grupo no tiene CUIT para buscar'); return; }
    setSearchingCuit(true);
    try {
      const exclude = casos.map(c => c.id).join(',');
      const r = await api.get('/case-duplicates/related', { params: { cuit: cuitDelGrupo, exclude_ids: exclude } });
      const nuevos = r.data?.casos || [];
      if (nuevos.length === 0) { toast.info('No se encontraron más coincidencias por CUIT'); }
      else {
        setCasos(prev => [...prev, ...nuevos]);
        setSelected(prev => { const n = new Set(prev); nuevos.forEach(c => n.add(c.id)); return n; });
        toast.success(`${nuevos.length} caso(s) adicional(es) por CUIT agregados`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar por CUIT');
    } finally {
      setSearchingCuit(false);
    }
  };

  const fusionar = async () => {
    const secundarios = [...selected].filter(id => id !== principalId);
    if (secundarios.length === 0) { toast.error('Elegí al menos un caso secundario'); return; }
    const principal = casos.find(c => c.id === principalId);
    if (!window.confirm(`Fusionar ${secundarios.length} caso(s) en «${principal?.titulo}» (estado ${principal?.estado})?\n\nLos secundarios se archivarán con nota de fusión.`)) return;
    setMerging(true);
    try {
      const r = await api.post('/case-duplicates/merge', { principal_id: principalId, secundarios_ids: secundarios });
      toast.success(`Fusionados ${r.data?.archivados?.length || 0} caso(s). El principal quedó con ${r.data?.total_alertas} alertas.`);
      onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo fusionar');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Card className="p-4" data-testid={`dup-group-${group.key_valor}`}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => { e.stopPropagation(); onToggleGroup(); }}
          onClick={(e) => e.stopPropagation()}
          title="Seleccionar este grupo para fusión múltiple"
          data-testid={`dup-group-select-${group.key_valor}`}
          className="cursor-pointer"
        />
        <div className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => setOpen(o => !o)}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Copy size={16} className="text-orange-500" />
          <span className="font-semibold text-slate-800">{group.key_tipo.toUpperCase()} {group.key_valor}</span>
          <span className="text-xs text-slate-500">· {casos.length} casos</span>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {casos.map(c => (
            <div key={c.id} className={`flex items-center gap-3 p-2 rounded-lg border ${c.id === principalId ? 'border-indigo-300 bg-indigo-50' : 'border-slate-100'}`} data-testid={`dup-case-${c.id}`}>
              <label className="flex items-center gap-1 text-xs cursor-pointer" title="Marcar como principal">
                <input type="radio" name={`principal-${group.key_valor}`} checked={c.id === principalId} onChange={() => setPrincipal(c.id)} data-testid={`dup-principal-${c.id}`} />
                <span className="font-medium">{c.id === principalId ? 'Principal' : 'Sec.'}</span>
              </label>
              {c.id !== principalId && (
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} title="Incluir en la fusión" data-testid={`dup-include-${c.id}`} />
              )}
              <button onClick={() => navigateToCase(c.id)} className="text-sm text-slate-700 hover:underline truncate max-w-[260px]" title={c.titulo}>{c.titulo}</button>
              <EstadoTag estado={c.estado} />
              <span className="text-[11px] text-slate-500">{c.total_alertas} alertas</span>
              <span className="text-[11px] text-slate-400 ml-auto">{c.responsable}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={buscarPorCuit} disabled={searchingCuit || !cuitDelGrupo} data-testid={`dup-search-cuit-${group.key_valor}`}>
              <Search size={14} className="mr-1" />{searchingCuit ? 'Buscando...' : 'Buscar por CUIT'}
            </Button>
            <Button size="sm" onClick={fusionar} disabled={merging} className="bg-indigo-600 hover:bg-indigo-700 ml-auto" data-testid={`dup-merge-${group.key_valor}`}>
              <GitMerge size={14} className="mr-1" />{merging ? 'Fusionando...' : `Fusionar (${[...selected].filter(id => id !== principalId).length})`}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// Navegación a un caso (best-effort; depende del módulo)
function navigateToCase(id) {
  window.open(`/m/gestion-de-casos/caso/${id}`, '_blank');
}

const groupKey = (g) => `${g.key_tipo}-${g.key_valor}`;

export default function CaseDuplicates() {
  const navigate = useNavigate();
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [bulkMerging, setBulkMerging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/case-duplicates');
      setGrupos(r.data?.grupos || []);
      setSelectedGroups(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar duplicados');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleGroup = (key) => {
    setSelectedGroups(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const mergeGroups = async (targetGroups, label) => {
    const validos = targetGroups.filter(g => (g.casos || []).filter(c => c.id !== g.sugerido_principal_id).length > 0);
    if (validos.length === 0) { toast.error('No hay grupos válidos para fusionar'); return; }
    if (!window.confirm(
      `Vas a fusionar ${validos.length} grupo(s) ${label}.\n\n` +
      `En cada grupo se fusionan los secundarios en el caso principal sugerido (el de más alertas) y se archivan. ¿Continuar?`
    )) return;
    setBulkMerging(true);
    let okGroups = 0, totalArchivados = 0, errores = 0;
    for (const g of validos) {
      const principal_id = g.sugerido_principal_id;
      const secundarios = (g.casos || []).filter(c => c.id !== principal_id).map(c => c.id);
      if (secundarios.length === 0) continue;
      try {
        const r = await api.post('/case-duplicates/merge', { principal_id, secundarios_ids: secundarios });
        okGroups++;
        totalArchivados += (r.data?.archivados?.length || 0);
      } catch {
        errores++;
      }
    }
    setBulkMerging(false);
    toast.success(`${okGroups} grupo(s) fusionados · ${totalArchivados} caso(s) archivados${errores ? ` · ${errores} con error` : ''}`);
    load();
  };

  const seleccionados = grupos.filter(g => selectedGroups.has(groupKey(g)));

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="case-duplicates-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={16} className="mr-1" /> Volver
        </Button>
        <div className="flex items-center gap-2">
          <Copy className="text-orange-500" size={24} />
          <h1 className="text-2xl font-bold text-slate-800">Casos duplicados</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {grupos.length > 0 && (
            <>
              <Button
                size="sm"
                onClick={() => mergeGroups(seleccionados, 'seleccionados')}
                disabled={bulkMerging || seleccionados.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="merge-selected-groups-btn"
              >
                <GitMerge size={14} className="mr-1" />
                {bulkMerging ? 'Fusionando...' : `Fusionar seleccionados (${seleccionados.length})`}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => mergeGroups(grupos, '(TODOS)')}
                disabled={bulkMerging}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="merge-all-groups-btn"
              >
                <GitMerge size={14} className="mr-1" />
                Fusionar TODOS ({grupos.length})
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="reload-btn">
            <RefreshCw size={16} className="mr-1" /> Actualizar
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-500">
        Casos no terminales que comparten el mismo CVU (o CUIT). Elegí el caso principal, opcionalmente sumá
        coincidencias por CUIT, y fusioná: las alertas se juntan en el principal y los secundarios se archivan.
        Para fusión <strong>múltiple</strong>, tildá los grupos y usá "Fusionar seleccionados" (o "Fusionar TODOS"),
        que usan el principal sugerido de cada grupo.
      </p>

      {loading ? (
        <p className="text-sm text-slate-400">Buscando duplicados...</p>
      ) : grupos.length === 0 ? (
        <Card className="p-8 text-center">
          <Copy size={32} className="text-green-500 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No se encontraron casos duplicados. 🎉</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="dup-groups-list">
          {grupos.map(g => (
            <DuplicateGroup
              key={groupKey(g)}
              group={g}
              onMerged={load}
              checked={selectedGroups.has(groupKey(g))}
              onToggleGroup={() => toggleGroup(groupKey(g))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
